import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { FileCategory, MediaSortMode, ProjectFile } from '../../../shared/models';
import { UploadResponse } from '../../../core/services/upload.types';

/** Where a media is referenced on the timeline — returned on a 409 delete body so the editor can warn. */
export interface MediaReferenceReport {
	referenced: boolean;
	/** `primaryBackground` scenes get deleted (media is their video background); the rest just lose the reference (e.g. a mask). */
	scenes: { chapterSlug: string | null; sceneSlug: string; primaryBackground: boolean }[];
	audioTracks: number;
}

/** Success body of a media delete; `cleaned` is present when a force delete removed references. */
export interface DeleteMediaResult {
	deleted: boolean;
	cleaned?: { scenesRemoved: number; scenesEdited: number; audioTracksRemoved: number };
}

interface MediaVersionResponse {
	label: string;
	width?: number;
	codec?: string;
	url: string;
	storageType: string;
	status: string;
}

interface VariantResponse {
	name: string;
	encodedName: string;
	versions: MediaVersionResponse[];
}

interface MediaResponse {
	_id: string;
	projectSlug: string;
	category: FileCategory;
	name: string;
	baseUrl: string;
	isVariantGroup: boolean;
	variants?: VariantResponse[];
	versions: MediaVersionResponse[];
	durationSeconds?: number;
	fps?: number;
	thumbnailUrl?: string;
}

export interface JobStatusResponse {
	jobId: string;
	type: 'versions' | 'video-mask' | 'thumbnail';
	mediaId: string;
	status: 'pending' | 'running' | 'done' | 'error';
	progress: number;
	currentStep: string;
	completedVersions: string[];
	totalVersions: number;
	error?: string;
	/** Per-item failures (e.g. one variant name's source file missing) that didn't abort the job — present alongside status:'done', not just status:'error'. */
	errorMessages?: string[];
	media?: MediaResponse;
}

/**
 * Outcome of the project-wide "generate missing resolutions" sweep. Everything
 * past `queued` is what the backend found incomplete but did not act on —
 * imported catalog docs (re-generating would destroy them), video-mask
 * composites (rebuilt from their sources instead), variant groups (DEV-741)
 * and images (repaired deliberately, since their job also rewrites references).
 */
export interface WidthSweepResult {
	queued: number;
	total: number;
	alreadyQueued: number;
	skippedImported: number;
	skippedComposites: number;
	skippedVariantGroups: number;
	skippedImages: number;
	declaredWidths: number[];
}

@Injectable()
export class EditorFilesService {
	private http = inject(HttpClient);

	readonly files = signal<ProjectFile[]>([]);
	readonly loading = signal(false);
	/** Per-category sort preference. Missing category = 'alphabetical' (default). */
	readonly sortModes = signal<Partial<Record<FileCategory, MediaSortMode>>>({});

	readonly groupedFiles = computed(() => {
		const all = this.files();
		const groups: Record<FileCategory, ProjectFile[]> = {
			'video': [],
			'mask': [],
			'video-mask': [],
			'images': [],
			'fonts': [],
			'audios': [],
			'subtitles': [],
		};
		for (const f of all) {
			groups[f.category].push(f);
		}
		return groups;
	});

	load(slug: string): void {
		this.projectSlug = slug;
		this.loading.set(true);
		this.http.get<MediaResponse[]>(`${environment.apiUrl}/api/projects/${slug}/media`).subscribe({
			next: (media) => {
				// Preserve `correlationId` across reloads so the file-browser
				// keeps matching done-but-undismissed upload-queue items to the
				// confirmed row (shared slot). Without this, `load()` wipes the
				// correlationId and a ghost placeholder reappears alongside the
				// confirmed file on the next render.
				const prevById = new Map(this.files().filter(f => f.correlationId).map(f => [f.id, f.correlationId]));
				this.files.set(media.map(m => {
					const file = this.toProjectFile(m);
					const cid = prevById.get(file.id);
					if (cid) file.correlationId = cid;
					return file;
				}));
				this.loading.set(false);
			},
			error: () => {
				this.files.set([]);
				this.loading.set(false);
			},
		});
		this.http.get<Partial<Record<FileCategory, MediaSortMode>>>(`${environment.apiUrl}/api/projects/${slug}/media/sort-modes`).subscribe({
			next: (modes) => this.sortModes.set(modes),
			error: () => this.sortModes.set({}),
		});
	}

	/** Switch a category's sort preference. Reloads the file list once the backend confirms — switching to 'custom' seeds the persisted order from the current alphabetical list. */
	setSortMode(category: FileCategory, mode: MediaSortMode): void {
		if (!this.projectSlug) return;
		this.http.patch<{ ok: boolean }>(
			`${environment.apiUrl}/api/projects/${this.projectSlug}/media/sort-mode`,
			{ category, mode },
		).subscribe({
			next: () => {
				this.sortModes.update(modes => ({ ...modes, [category]: mode }));
				this.reloadCurrent();
			},
		});
	}

	/**
	 * Persist a drag-and-drop reorder within one category. Dragging to reorder
	 * is always available (like the existing drag-to-timeline gesture) — it
	 * implicitly switches that category to 'custom' mode so the new order
	 * sticks instead of being overwritten by the next alphabetical reload.
	 * Reorders the local `files`/`sortModes` signals immediately so the grid
	 * reflects the drop without waiting on the round-trip.
	 */
	reorderCategory(category: FileCategory, orderedIds: string[]): void {
		if (!this.projectSlug) return;
		// `groupedFiles()` re-buckets by category, so only the relative order
		// within `category` matters — a stable sort (guaranteed by spec) that
		// only ever reorders same-category pairs leaves everything else as-is.
		const rank = new Map(orderedIds.map((id, index) => [id, index]));
		this.files.update(current => [...current].sort((a, b) => {
			if (a.category !== category || b.category !== category || !a.id || !b.id) return 0;
			return (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0);
		}));
		this.sortModes.update(modes => ({ ...modes, [category]: 'custom' }));
		this.http.patch<{ ok: boolean }>(
			`${environment.apiUrl}/api/projects/${this.projectSlug}/media/reorder`,
			{ category, orderedIds },
		).subscribe();
	}

	/** Reload files for the current project slug (silent — no loading flash). */
	reloadCurrent(): void {
		this.reloadAndThen();
	}

	/**
	 * Like reloadCurrent, but invokes `after()` once the fresh files are in
	 * the signal. Callers use this to perform follow-up work that needs the
	 * post-reload state (e.g. diffing old vs new baseUrl).
	 */
	reloadAndThen(after?: () => void): void {
		if (!this.projectSlug) return;
		this.http.get<MediaResponse[]>(`${environment.apiUrl}/api/projects/${this.projectSlug}/media`).subscribe({
			next: (media) => {
				const prevById = new Map(this.files().filter(f => f.correlationId).map(f => [f.id, f.correlationId]));
				const updated = media.map(m => {
					const file = this.toProjectFile(m);
					const cid = prevById.get(file.id);
					if (cid) file.correlationId = cid;
					return file;
				});
				this.files.set(updated);
				after?.();
			},
		});
	}

	/**
	 * Push a freshly-uploaded file into the local files signal so the grid can
	 * render the confirmed row instantly — no GET /media round-trip.
	 *
	 * If a row with the same id is already present, update in place (this is
	 * the replace-in-place path: the backend kept the same `_id` + `baseUrl`
	 * but reset versions and reprobed metadata; consumers reading the signal
	 * pick up the new metadata without a list reorder).
	 *
	 * `duplicateWarning` is cleared when an upload successfully replaced an
	 * existing row — the warning was about a pre-upload conflict that's now
	 * resolved.
	 */
	addLocalFile(response: UploadResponse, correlationId?: string, duplicateWarning?: boolean): void {
		const isVideoCat = response.category === 'video' || response.category === 'mask' || response.category === 'video-mask';
		const baseFile: ProjectFile = {
			id: response.id,
			category: response.category,
			name: response.name,
			baseUrl: response.baseUrl,
			previewUrl: response.thumbnailUrl ?? response.baseUrl,
			videoUrl: isVideoCat ? response.baseUrl : undefined,
			isLocal: true,
			durationSeconds: response.durationSeconds,
			fps: response.fps,
			versionCount: 1,
			correlationId,
			duplicateWarning: response.replaced ? false : duplicateWarning,
		};
		this.files.update(current => {
			const idx = current.findIndex(f => f.id === baseFile.id);
			if (idx === -1) return [...current, baseFile];
			// Update in place — preserve list ordering. The replaced row keeps
			// its slot in the grid, no flicker, no reorder.
			const next = current.slice();
			next[idx] = baseFile;
			return next;
		});
	}

	/**
	 * Upload a local file to the backend. Optionally pass pre-probed metadata
	 * (from mediabunny) to skip backend ffprobe.
	 */
	upload(
		file: File,
		category: 'video' | 'mask',
		metadata?: { durationSeconds?: number; fps?: number; width?: number; height?: number },
	): Observable<MediaResponse> {
		if (!this.projectSlug) throw new Error('EditorFilesService.upload: projectSlug not set — call load() first');
		const form = new FormData();
		form.append('file', file);
		form.append('category', category);
		if (metadata?.durationSeconds != null) form.append('durationSeconds', String(metadata.durationSeconds));
		if (metadata?.fps != null) form.append('fps', String(metadata.fps));
		if (metadata?.width != null) form.append('width', String(metadata.width));
		if (metadata?.height != null) form.append('height', String(metadata.height));
		return this.http.post<MediaResponse>(
			`${environment.apiUrl}/api/projects/${this.projectSlug}/media/upload`,
			form,
		);
	}

	/** Delete a locally-stored media entry (backend removes DB doc + disk folder). */
	/**
	 * Delete a media doc + files. Without `force`, the backend returns 409 (with a
	 * `references` report on the error body) when the media is still referenced on
	 * the timeline; with `force`, it first removes those references (deletes
	 * referencing scenes, drops audio-track entries) so nothing is orphaned. The
	 * success body's `cleaned` says what was removed. */
	deleteMedia(mediaId: string, opts?: { force?: boolean }): Observable<DeleteMediaResult> {
		const url = `${environment.apiUrl}/api/media/${mediaId}${opts?.force ? '?force=true' : ''}`;
		return this.http.delete<DeleteMediaResult>(url);
	}

	/** Find a loaded file by its baseUrl (absolute URL). Returns undefined if not in the pool (e.g. external URL not in media catalog). */
	findByBaseUrl(url: string): ProjectFile | undefined {
		return this.files().find(f => f.baseUrl === url);
	}

	/** Kick off version generation for a media. Returns jobId. `onlyMissing` fills
	 * in the levels the doc owes instead of rebuilding every one of them. */
	generateVersions(mediaId: string, opts?: { widths?: number[]; onlyMissing?: boolean }): Observable<{ jobId: string; status: string }> {
		return this.http.post<{ jobId: string; status: string }>(
			`${environment.apiUrl}/api/media/${mediaId}/generate-versions`,
			opts ?? {},
		);
	}

	/** Queue a 'versions' job for every audio doc in the project missing its mp3/aac codec sibling — same mechanism as generateVersions(), just enumerated project-wide instead of one mediaId at a time. */
	generateMissingAudioCodecs(projectSlug: string): Observable<{ queued: number; total: number }> {
		return this.http.post<{ queued: number; total: number }>(
			`${environment.apiUrl}/api/projects/${projectSlug}/media/generate-missing-audio-codecs`,
			{},
		);
	}

	/** Queue a 'versions' job for every image doc in the project whose baseUrl doesn't match any of its own generated versions (PRO-198 — see backend image-baseurl-repair-queue.service.ts). */
	repairBrokenImageBaseUrls(projectSlug: string): Observable<{ queued: number; total: number }> {
		return this.http.post<{ queued: number; total: number }>(
			`${environment.apiUrl}/api/projects/${projectSlug}/media/repair-broken-image-baseurls`,
			{},
		);
	}

	/** Queue an incremental 'versions' job for every media missing a resolution the
	 * project declares (DEV-688 — see backend width-coverage-queue.service.ts). The
	 * skipped* counts say what was found but deliberately left alone. */
	generateMissingWidths(projectSlug: string): Observable<WidthSweepResult> {
		return this.http.post<WidthSweepResult>(
			`${environment.apiUrl}/api/projects/${projectSlug}/media/generate-missing-widths`,
			{},
		);
	}

	/** Poll a job status. */
	getJob(jobId: string): Observable<JobStatusResponse> {
		return this.http.get<JobStatusResponse>(`${environment.apiUrl}/api/media/jobs/${jobId}`);
	}

	/** Create a video_mask composite from a video + mask pair. Returns jobId + newMediaId. */
	createVideoMask(videoMediaId: string, maskMediaId: string): Observable<{ jobId: string; newMediaId: string; status: string }> {
		return this.http.post<{ jobId: string; newMediaId: string; status: string }>(
			`${environment.apiUrl}/api/media/video-mask/create`,
			{ videoMediaId, maskMediaId },
		);
	}

	/** Create an empty variant group. */
	createVariantGroup(category: FileCategory, name: string): Observable<MediaResponse> {
		if (!this.projectSlug) throw new Error('EditorFilesService: projectSlug not set — call load() first');
		return this.http.post<MediaResponse>(
			`${environment.apiUrl}/api/projects/${this.projectSlug}/media/create-variant-group`,
			{ category, name },
		);
	}

	/**
	 * Upload a single file into an existing variant group with its own probed
	 * metadata. Returns the updated media document including all variants.
	 *
	 * The multi-file shape is intentionally gone from the frontend — variant
	 * uploads now flow through `UploadService.enqueueVariantGroup()` so they
	 * surface in the upload panel with per-file progress and per-file metadata
	 * (plan D8 / D9). Callers uploading variants should enqueue, not invoke this
	 * service directly. This method is kept for direct programmatic use (tests,
	 * one-off scripts); the backend endpoint still accepts multi-file batches
	 * for API compatibility.
	 */
	uploadVariant(
		groupMediaId: string,
		file: File,
		metadata?: { durationSeconds?: number; fps?: number; width?: number; height?: number },
	): Observable<{ added: number; errors: string[]; media: MediaResponse }> {
		if (!this.projectSlug) throw new Error('EditorFilesService: projectSlug not set — call load() first');
		const form = new FormData();
		form.append('files', file);
		if (metadata?.durationSeconds != null) form.append('durationSeconds', String(metadata.durationSeconds));
		if (metadata?.fps != null) form.append('fps', String(metadata.fps));
		if (metadata?.width != null) form.append('width', String(metadata.width));
		if (metadata?.height != null) form.append('height', String(metadata.height));
		return this.http.post<{ added: number; errors: string[]; media: MediaResponse }>(
			`${environment.apiUrl}/api/projects/${this.projectSlug}/media/${groupMediaId}/variants/upload`,
			form,
		);
	}

	/** Delete a single variant from a group. */
	deleteVariant(groupMediaId: string, encodedName: string): Observable<void> {
		if (!this.projectSlug) throw new Error('EditorFilesService: projectSlug not set — call load() first');
		return this.http.delete<void>(
			`${environment.apiUrl}/api/projects/${this.projectSlug}/media/${groupMediaId}/variants/${encodedName}`,
		);
	}

	renameMedia(mediaId: string, name: string): Observable<{ ok: boolean; name: string }> {
		if (!this.projectSlug) throw new Error('EditorFilesService: projectSlug not set — call load() first');
		return this.http.patch<{ ok: boolean; name: string }>(
			`${environment.apiUrl}/api/projects/${this.projectSlug}/media/${mediaId}`,
			{ name },
		);
	}

	private projectSlug = '';

	private toProjectFile(media: MediaResponse): ProjectFile {
		// Preview URL: thumbnail for videos, smallest version for others, fallback to baseUrl
		const previewVersion = this.pickPreviewVersion(media.versions);
		const videoVersionUrl = previewVersion?.url ?? media.baseUrl;
		const previewUrl = media.thumbnailUrl ?? videoVersionUrl;
		const isLocal = media.versions.some(v => v.storageType === 'local')
			|| (media.isVariantGroup && (
				(media.variants ?? []).length === 0
				|| (media.variants ?? []).some(vr => vr.versions.some(v => v.storageType === 'local'))
			));
		const isVideo = media.category === 'video' || media.category === 'mask' || media.category === 'video-mask';

		return {
			id: media._id,
			category: media.category,
			name: media.name,
			baseUrl: media.baseUrl,
			previewUrl,
			videoUrl: isVideo ? videoVersionUrl : undefined,
			isLocal,
			durationSeconds: media.durationSeconds,
			fps: media.fps,
			versionCount: media.isVariantGroup
				? Math.max(media.versions.length, ...((media.variants ?? []).map(v => v.versions.length)))
				: media.versions.length,
			variants: media.isVariantGroup && media.variants
				? media.variants.map(v => ({
					name: v.name,
					encodedName: v.encodedName,
					url: this.pickPreviewVersion(v.versions)?.url ?? '',
				}))
				: undefined,
		};
	}

	/** Pick the smallest version for preview (prefer mp4 codec for videos) */
	private pickPreviewVersion(versions: MediaVersionResponse[]): MediaVersionResponse | undefined {
		if (!versions.length) return undefined;
		const candidates = versions.filter(v => v.codec === 'mp4' || !v.codec);
		const pool = candidates.length ? candidates : versions;
		return pool.reduce((a, b) => (a.width ?? 0) < (b.width ?? 0) ? a : b);
	}
}
